<script>
import axios from 'axios';

function requestKey() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}`;
}

export default {
  props: { user: {type: Object, required: true} },
  data() {
    return {open: false, loading: false, submitting: false, error: '', entries: [], banks: [],
      selected: [], bank: '', balance: 0, minimum: 100, key: null, fingerprint: null};
  },
  computed: {
    chosen() { return this.entries.filter(item => this.selected.includes(item.installment_id)); },
    totalCents() { return this.chosen.reduce((total, item) => total + Math.round(Number(item.net_value) * 100), 0); },
    valid() {
      return this.selected.length > 0 && this.selected.length <= 100 && !!this.bank &&
        this.totalCents >= Math.round(this.minimum * 100) && this.totalCents <= Math.round(this.balance * 100);
    }
  },
  methods: {
    headers() {
      const actor = JSON.parse(localStorage.getItem('user'));
      return {Accept: 'application/json', Authorization: `Bearer ${actor.access_token}`};
    },
    money(value) { return Number(value).toLocaleString('pt-BR', {style: 'currency', currency: 'BRL'}); },
    destination(bank) {
      return bank.pix_key ? `Pix (${bank.pix_type || 'chave'}): ${bank.pix_key}` :
        `${bank.bank || bank.bank_code} — Agência ${bank.agency} / Conta ${bank.account}`;
    },
    async show() {
      if (this.loading || this.submitting) return;
      this.open = true; this.loading = true; this.error = ''; this.selected = []; this.bank = '';
      this.entries = []; this.banks = []; this.key = null; this.fingerprint = null;
      try {
        const {data} = await axios.get(`${process.env.VUE_APP_API}api/v1/admin/cashout/user/${this.user.uuid}/options`, {headers: this.headers()});
        if (!data.success) throw new Error(data.message || 'Não foi possível carregar as parcelas.');
        this.entries = data.data.installments.filter(item => item.state === 'available');
        this.banks = data.data.bank_accounts; this.balance = Number(data.data.balance); this.minimum = Number(data.data.minimum);
      } catch (error) { this.error = error.response && error.response.data.message || error.message; }
      finally { this.loading = false; }
    },
    async submit() {
      if (this.submitting || !this.valid) return;
      this.submitting = true; this.error = '';
      try {
        const ids = this.selected.slice().sort((a,b) => a-b);
        const fingerprint = JSON.stringify([this.user.uuid, Number(this.bank), ids]);
        if (fingerprint !== this.fingerprint) { this.key = requestKey(); this.fingerprint = fingerprint; }
        const {data} = await axios.post(`${process.env.VUE_APP_API}api/v1/admin/cashout/user/${this.user.uuid}`, {
          bank_account_id: Number(this.bank), installment_ids: ids, request_key: this.key
        }, {headers: this.headers()});
        if (!data.success) throw new Error(data.message || 'Não foi possível solicitar o saque.');
        this.open = false;
        this.$emit('requested', data.data);
        this.$router.push(`/cashout/${data.data.cashout.id}`);
      } catch (error) { this.error = error.response && error.response.data.message || error.message; }
      finally { this.submitting = false; }
    }
  }
};
</script>

<template>
  <div class="mb-3">
    <b-button variant="primary" @click="show" :disabled="loading || submitting || !!(user.wallet && user.wallet.payment_review_required)">Solicitar saque para o usuário</b-button>
    <b-modal v-model="open" title="Solicitar saque pelo financeiro" size="lg" hide-footer
      :no-close-on-backdrop="loading || submitting" :no-close-on-esc="loading || submitting" :hide-header-close="loading || submitting">
      <p><strong>Titular: {{user.name}}</strong></p>
      <p>O pedido ficará pendente, identificado como <strong>Solicitado pelo financeiro</strong>. Não realiza transferência bancária.</p>
      <b-alert show variant="danger" v-if="error">{{error}}</b-alert>
      <p v-if="loading">Carregando parcelas e contas do usuário...</p>
      <template v-else>
        <p>Saldo: {{money(balance)}} · Mínimo: {{money(minimum)}}</p>
        <div class="table-responsive" style="max-height: 320px; overflow: auto">
          <table class="table table-sm">
            <thead><tr><th>Selecionar</th><th>Animal / cobrança</th><th>Parcela</th><th>Líquido</th></tr></thead>
            <tbody><tr v-for="item in entries" :key="item.installment_id">
              <td><input type="checkbox" v-model="selected" :value="item.installment_id" :disabled="submitting" :aria-label="'Selecionar ' + item.description" /></td>
              <td>{{item.description}}</td><td>{{item.parcel_number ? 'Parcela ' + item.parcel_number : 'Entrada'}}</td>
              <td>{{money(item.net_value)}}<small v-if="item.used_value" class="d-block">{{money(item.used_value)}} já utilizados em pagamentos</small></td>
            </tr></tbody>
          </table>
        </div>
        <p v-if="!entries.length">Nenhuma parcela disponível para saque.</p>
        <label for="finance-bank">Conta de destino do usuário</label>
        <select id="finance-bank" class="form-control" v-model="bank" :disabled="submitting">
          <option value="">Selecione uma conta</option>
          <option v-for="account in banks" :key="account.id" :value="account.id">{{destination(account)}}</option>
        </select>
        <p v-if="!banks.length">O usuário precisa cadastrar uma conta bancária ou chave Pix antes de solicitar o saque.</p>
        <h5 class="mt-3">Total solicitado: {{money(totalCents / 100)}}</h5>
        <p>Selecione até 100 parcelas. A confirmação da transferência será feita separadamente, com comprovante.</p>
        <b-button variant="primary" :disabled="!valid || submitting" @click="submit">{{submitting ? 'Solicitando...' : 'Confirmar solicitação pelo financeiro'}}</b-button>
      </template>
    </b-modal>
  </div>
</template>
